/**
 * Helpers for files attached to an open revision request ("revision content").
 *
 * The server decides a file's type from its extension, and rejects a
 * `content_type` that disagrees with it (422 `type_mismatch`). The browser's own
 * MIME guess is not reliable for this - `.ogg` is commonly reported as
 * `video/ogg` but the server lists it under `audio` - so the upload never sends
 * `content_type` for this category and this table is used only to decide, in the
 * browser, whether a file is worth uploading at all.
 *
 * Source of truth: `RevisionContent::TYPES` in lucihub-rails.
 */

export const REVISION_CONTENT_EXTENSIONS = {
  video: ['.mp4', '.mov', '.m4v', '.avi', '.mkv', '.mxf', '.webm', '.mts', '.m2ts', '.3gp', '.wmv', '.mpg', '.mpeg'],
  image: ['.jpg', '.jpeg', '.png', '.heic', '.heif', '.webp', '.gif', '.tif', '.tiff', '.bmp', '.dng', '.cr2', '.cr3', '.nef', '.arw', '.raw'],
  audio: ['.mp3', '.wav', '.m4a', '.aac', '.aif', '.aiff', '.flac', '.ogg', '.wma'],
  document: ['.pdf', '.doc', '.docx', '.txt', '.rtf', '.ppt', '.pptx', '.xls', '.xlsx', '.csv', '.key', '.pages', '.numbers'],
};

/**
 * Dropzone `accept` map. react-dropzone flattens this to a list of MIME patterns
 * and extensions and matches a file against any of them, so the extension lists
 * are what actually decide - the MIME keys only group them readably and feed the
 * native picker's filter.
 */
export const REVISION_CONTENT_ACCEPT = {
  'video/*': REVISION_CONTENT_EXTENSIONS.video,
  'image/*': REVISION_CONTENT_EXTENSIONS.image,
  'audio/*': REVISION_CONTENT_EXTENSIONS.audio,
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/rtf': ['.rtf'],
  'text/plain': ['.txt'],
  'text/csv': ['.csv'],
  'application/octet-stream': ['.key', '.pages', '.numbers'],
};

/** Lower-cased extension including the dot, or '' when the name has none. */
export const fileExtension = (filename) => {
  const name = `${filename || ''}`;
  const dot = name.lastIndexOf('.');
  // A leading dot is a hidden file, not an extension.
  if (dot <= 0) return '';
  return name.slice(dot).toLowerCase();
};

/**
 * 'video' | 'image' | 'audio' | 'document', or null when the extension is not in
 * the table (which the server answers with 422 `unsupported_type`). A name with
 * no extension is also unsupported.
 */
export const revisionContentType = (filename) => {
  const extension = fileExtension(filename);
  if (!extension) return null;
  const match = Object.keys(REVISION_CONTENT_EXTENSIONS)
    .find((type) => REVISION_CONTENT_EXTENSIONS[type].includes(extension));
  return match || null;
};

export const isSupportedRevisionContent = (filename) => revisionContentType(filename) !== null;

/** Human file size. The API returns bytes; 0 and null both read as "—". */
export const formatFileSize = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = size;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  // Bytes and KB read better whole; anything larger keeps one decimal.
  const rounded = unit < 2 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[unit]}`;
};

/**
 * `round` is null while the revision is open, so a round label must never be
 * invented for one - the open request is labelled as such.
 */
export const roundLabel = (round, submitted) => {
  if (round) return round;
  return submitted ? 'Submitted' : 'Open request';
};

/** `content_counts` -> "2 files" / "1 file" / "No files". */
export const contentCountLabel = (counts) => {
  const total = Number(counts?.total);
  if (!Number.isFinite(total) || total <= 0) return 'No files';
  return total === 1 ? '1 file' : `${total} files`;
};

/**
 * Error codes the server attaches to a coded failure. Branch on these, never on
 * the message text.
 */
export const REVISION_ERROR_CODE = {
  REVISION_SUBMITTED: 'revision_submitted',
  UPLOAD_CLOSED: 'upload_closed',
  FORBIDDEN: 'forbidden',
  INVALID_FILE: 'invalid_file',
  UNSUPPORTED_TYPE: 'unsupported_type',
  TYPE_MISMATCH: 'type_mismatch',
  PART_TOO_SMALL: 'part_too_small',
  UPLOAD_COMPLETED: 'upload_completed',
  UPLOAD_ABORTED: 'upload_aborted',
  UPLOAD_CORRUPT: 'upload_corrupt',
};

/**
 * Chunk failures that must stop the file immediately rather than go round the
 * retry/backoff loop. Everything else (422 other than a bad index, and 5xx)
 * keeps the existing five-attempt behaviour.
 */
export const isNonRetryableChunkFailure = ({ httpStatus, errorCode, message } = {}) => {
  if ([
    REVISION_ERROR_CODE.REVISION_SUBMITTED,
    REVISION_ERROR_CODE.UPLOAD_ABORTED,
    REVISION_ERROR_CODE.UPLOAD_COMPLETED,
    REVISION_ERROR_CODE.PART_TOO_SMALL,
  ].includes(errorCode)) return true;

  if ([403, 404, 413, 415].includes(Number(httpStatus))) return true;

  // "Invalid chunk index" is a client bug - retrying sends the same bad index.
  if (Number(httpStatus) === 422 && /invalid chunk index/i.test(`${message || ''}`)) return true;

  return false;
};

/**
 * The status line shown against a file that stopped. Specific where the server
 * was specific, so the user knows whether to re-upload or give up.
 */
export const chunkFailureStatus = ({ httpStatus, errorCode, message } = {}) => {
  switch (errorCode) {
    case REVISION_ERROR_CODE.REVISION_SUBMITTED:
      return 'Revision was submitted — not added';
    case REVISION_ERROR_CODE.UPLOAD_ABORTED:
      return 'Upload cancelled — please upload again';
    case REVISION_ERROR_CODE.UPLOAD_CORRUPT:
      return 'File did not match its size — upload again';
    case REVISION_ERROR_CODE.PART_TOO_SMALL:
      return 'Upload failed — chunk size';
    case REVISION_ERROR_CODE.UPLOAD_CLOSED:
    case REVISION_ERROR_CODE.FORBIDDEN:
      return message || 'Upload not allowed';
    default:
      break;
  }
  if (Number(httpStatus) === 415) return message || 'File was rejected as unsafe';
  if (Number(httpStatus) === 404) return 'Upload not found';
  return 'Upload Failed!!';
};

/**
 * Message for a failed pre_flight, list or delete. The server's own message is
 * already written for the user on every coded error, so it is preferred; the
 * fallbacks only cover a response that arrived without one.
 */
export const revisionContentErrorMessage = (httpCode, message, errorCode) => {
  if (message) return message;
  switch (errorCode) {
    case REVISION_ERROR_CODE.REVISION_SUBMITTED:
      return 'This revision has already been submitted.';
    case REVISION_ERROR_CODE.INVALID_FILE:
      return 'Invalid file type!!!';
    case REVISION_ERROR_CODE.UNSUPPORTED_TYPE:
      return 'Unsupported file type';
    default:
      break;
  }
  switch (Number(httpCode)) {
    case 403:
      return "You don't have access to these files";
    case 404:
      return 'Not found';
    default:
      return 'Something went wrong';
  }
};

/**
 * The listing returns every round for a user. The open request is the one that
 * accepts uploads, and is identified by `revision_id` rather than by position -
 * a project can hold several rounds and only one of them is open.
 */
export const findRound = (rounds, revisionId) => {
  if (!Array.isArray(rounds)) return null;
  return rounds.find((round) => round?.revision_id === revisionId) || null;
};
